import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, effect, ElementRef, inject, Input, OnDestroy, output, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { fromEvent, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { UserTheme } from '@bn/primitives';
import { ContextTextClassPipe } from '@bn/shared/core/pipes';

import { BadgeComponent } from '../badge/badge.component';
import { UploadFileDialogDirective } from '../dialog/directives/upload-file-dialog.directive';
import { ModalState } from '../dialog/models/modal-state.model';
import { BnDropdownService } from '../dropdown-menu/dropdown.service';
import { SwitchComponent } from '../switch/switch.component';
import { ThemeService } from '../theme/services/theme.service';

import { DropdownMenuItem, DropDownMenuOptions } from './models';

const TRIGGER_CLASS = 'bn-dropdown__trigger';

@Component({
  selector: 'bn-web-ui-dropdown-menu',
  templateUrl: './dropdown-menu.component.html',
  styleUrls: ['./dropdown-menu.component.scss'],
  imports: [
    NgClass,
    NgStyle,
    FormsModule,
    BadgeComponent,
    SwitchComponent,
    FaIconComponent,
    TooltipDirective,
    ContextTextClassPipe,
    RouterLink,
    UploadFileDialogDirective,
    NgTemplateOutlet,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
  ],
})
export class DropdownMenuComponent implements OnDestroy {
  private readonly elementRef = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);

  public readonly dropdownTrigger = viewChild.required<ElementRef<HTMLElement>>('dropdownTrigger');

  public contentPosition?: { x: number; y: number };

  public fixedPosition?: { top: number; left?: number; right?: number };

  @Input()
  public hasCustomPosition = false;

  @Input()
  public set items(items: DropdownMenuItem[]) {
    this.dropdownItems = items;
  }

  @Input()
  public set options(optionsValues: DropDownMenuOptions) {
    this.menuOptions = {
      ...this.defaultOptions,
      ...optionsValues,
    };
  }

  @Input()
  public aboveLoader = false;

  @Input()
  public hasContentShadow = false;

  @Input()
  public set disabled(value: boolean | undefined) {
    this.isDisabled = value !== false && value !== null && value !== undefined;

    if (this.isDisabled && this.isOpened) {
      this.closeMenu();
      this.dropDownStateChanged.emit(false);
    }
  }

  @Input()
  public uploadData?: ModalState;

  public readonly dropDownStateChanged = output<boolean>();

  public readonly clickAction = output<DropdownMenuItem>();

  public isDarkTheme = false;
  public isOpened = false;
  public isDisabled = false;
  public menuOptions: DropDownMenuOptions = {};
  public dropdownItems!: DropdownMenuItem[];
  private clickOutsideSubscription = new Subscription();
  private closeMenuSubscription = new Subscription();
  private escKeySubscription = new Subscription();
  private repositionSubscription = new Subscription();
  private static readonly FIXED_MENU_GAP = 8;
  private static readonly FIXED_MENU_Z_INDEX = 1050;

  private uuid = crypto.randomUUID();
  private readonly defaultOptions: DropDownMenuOptions = {
    align: 'start',
    closeOnItemClick: true,
    position: 'bottom',
    size: 'auto',
  };
  private readonly bnDropdownService = inject(BnDropdownService);
  readonly #themeService = inject(ThemeService);

  constructor() {
    this.closeMenuSubscription.add(
      this.bnDropdownService.getCloseAllDropdowns$().subscribe((uuid: string) => {
        if (this.isOpened && uuid !== this.uuid) {
          this.closeMenu();
        }
      }),
    );

    effect(() => {
      const theme = this.#themeService.getCurrentTheme();
      this.isDarkTheme = theme === UserTheme.dark;
    });
  }

  public ngOnDestroy(): void {
    this.clickOutsideSubscription.unsubscribe();
    this.closeMenuSubscription.unsubscribe();
    this.escKeySubscription.unsubscribe();
    this.repositionSubscription.unsubscribe();
  }

  public toggleDropdown(triggerClickEvent?: MouseEvent): void {
    if (this.isDisabled) {
      triggerClickEvent?.preventDefault();
      triggerClickEvent?.stopPropagation();
      return;
    }

    triggerClickEvent?.stopPropagation();

    // right mouse click is not registered as a click event
    if (!triggerClickEvent) {
      this.dropdownTrigger()?.nativeElement?.classList.remove(TRIGGER_CLASS); // remove arrow on right click
    } else {
      const dropdownTrigger = this.dropdownTrigger();
      if (!dropdownTrigger?.nativeElement?.classList.contains(TRIGGER_CLASS)) {
        dropdownTrigger?.nativeElement?.classList.add(TRIGGER_CLASS);
      }
    }

    this.isOpened = !this.isOpened;

    if (this.isOpened) {
      this.bnDropdownService.closeAllDropdowns(this.uuid);

      if (this.menuOptions.overflowEscape) {
        this.setupFixedPosition();
      }

      this.clickOutsideSubscription = fromEvent<MouseEvent>(document, 'click').subscribe((event) => this.onDropdownMenuFocusOut(event));

      this.escKeySubscription = fromEvent<KeyboardEvent>(window, 'keydown')
        .pipe(filter((event) => event.key === 'Escape'))
        .subscribe((event) => {
          if (this.isOpened && !this.elementRef.nativeElement.contains(event.target)) {
            this.toggleDropdown();
          }
        });
    } else {
      this.clickOutsideSubscription.unsubscribe();
      this.closeMenu();
    }

    this.dropDownStateChanged.emit(this.isOpened);
  }

  // clear positions in case menu was opened manually
  private closeMenu(): void {
    this.isOpened = false;
    this.contentPosition = undefined;
    this.fixedPosition = undefined;
    this.hasCustomPosition = false;
    this.repositionSubscription.unsubscribe();
    this.cdr.detectChanges();
  }

  public get isFixedPosition(): boolean {
    return !!(this.menuOptions.overflowEscape && this.fixedPosition);
  }

  public trackMenuItem(index: number, item: DropdownMenuItem): string | number {
    return item.id ?? item.label ?? index;
  }

  public get viewportHeight(): number {
    const virtualScroll = this.menuOptions.virtualScroll;

    if (!virtualScroll) return 0;

    return Math.min(virtualScroll.maxHeight, (this.dropdownItems?.length ?? 0) * virtualScroll.itemHeight);
  }

  public get contentStyle(): Record<string, string | number | undefined> {
    if (this.isFixedPosition) {
      return {
        position: 'fixed',
        'top.px': this.fixedPosition?.top,
        'left.px': this.fixedPosition?.left,
        'right.px': this.fixedPosition?.right,
        'z-index': this.menuOptions.zIndex ?? DropdownMenuComponent.FIXED_MENU_Z_INDEX,
      };
    }

    return {
      'margin-left': this.menuOptions.align === 'start' ? this.menuOptions.indent || '' : '',
      'margin-right': this.menuOptions.align === 'end' ? this.menuOptions.indent || '' : '',
      'top.px': this.contentPosition?.y,
      'left.px': this.contentPosition?.x,
      'z-index': this.menuOptions.zIndex,
    };
  }

  private setupFixedPosition(): void {
    const triggerElement = this.dropdownTrigger()?.nativeElement as HTMLElement | undefined;
    const triggerRect = triggerElement?.getBoundingClientRect();
    if (!triggerRect) return;

    const top = triggerRect.bottom + DropdownMenuComponent.FIXED_MENU_GAP;

    this.fixedPosition = this.menuOptions.align === 'end' ? { top, right: window.innerWidth - triggerRect.right } : { top, left: triggerRect.left };

    this.repositionSubscription = new Subscription();
    this.repositionSubscription.add(
      fromEvent<Event>(window, 'scroll', { capture: true })
        // Scrolling the menu's own option list (e.g. the virtual-scroll viewport) also dispatches a
        // 'scroll' event that reaches this capture-phase listener — only a scroll outside the dropdown
        // itself should reposition/close it.
        .pipe(filter((event) => !this.elementRef.nativeElement.contains(event.target as Node)))
        .subscribe(() => this.forceClose()),
    );
    this.repositionSubscription.add(fromEvent(window, 'resize').subscribe(() => this.forceClose()));
  }

  private forceClose(): void {
    if (!this.isOpened) return;
    this.clickOutsideSubscription.unsubscribe();
    this.closeMenu();
    this.dropDownStateChanged.emit(false);
  }

  public onLinkClicked(menuItem: DropdownMenuItem, event: MouseEvent): void {
    event.stopPropagation();

    this.clickAction.emit(menuItem);

    if (this.menuOptions.closeOnItemClick) {
      this.closeMenu();
    }
  }

  public toggleTheme(): void {
    this.#themeService.setTheme(this.isDarkTheme ? UserTheme.dark : UserTheme.light);
  }

  public onDropdownMenuFocusOut(event: MouseEvent): void {
    const itemEventPath: EventTarget[] = (event.composedPath() as Element[]).filter(
      (item) => item.className?.length && (item.className.includes('bn-dropdown__item') || item.className.includes('bn-dropdown__header')),
    );

    if (
      (this.isOpened && !this.elementRef.nativeElement.contains(event.target)) ||
      (this.isOpened && this.elementRef.nativeElement.contains(event.target) && this.menuOptions?.closeOnItemClick && itemEventPath.length)
    ) {
      this.toggleDropdown(event);
    }
  }
}
